import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Users, FolderKanban, User, Bot, Building2, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVaultStore } from "@/stores/billing/vault-store";
import type { TokenVault, VaultAssignment as VaultAssignmentType } from "@/types/billing";
import { VaultTargetPicker } from "./VaultTargetPicker";

/**
 * VaultAssignment
 * Manages vault target assignments (users, groups, apps, agents).
 * Shows current assignments in a table and provides an add dialog.
 */

interface VaultAssignmentProps {
  vault: TokenVault;
}

const TARGET_TYPE_CONFIG: Record<
  VaultAssignmentType["targetType"],
  { label: string; icon: React.FC<React.SVGProps<SVGSVGElement>>; color: string }
> = {
  user: {
    label: "User",
    icon: User,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  group: {
    label: "Group",
    icon: Users,
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  },
  app: {
    label: "App",
    icon: FolderKanban,
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  agent: {
    label: "Agent",
    icon: Bot,
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  },
  project: {
    label: "Project",
    icon: FolderKanban,
    color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  },
  page: {
    label: "Page",
    icon: FileText,
    color: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  },
  org: {
    label: "Organization",
    icon: Building2,
    color: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300",
  },
};

const VAULT_TYPE_PRIORITY: Record<string, number> = {
  team: 0,
  project: 1,
  personal: 2,
  general: 3,
};

function isVaultTargetType(value: string): value is VaultAssignmentType["targetType"] {
  return Object.keys(TARGET_TYPE_CONFIG).includes(value);
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export const VaultAssignment: React.FC<VaultAssignmentProps> = ({ vault }) => {
  const { assignToVault, removeFromVault, isLoading } = useVaultStore();

  const [addOpen, setAddOpen] = useState(false);
  const [targetType, setTargetType] = useState<VaultAssignmentType["targetType"]>("user");
  const [targetId, setTargetId] = useState("");
  const [priority, setPriority] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setTargetType("user");
    setTargetId("");
    setPriority("");
  };

  const handleAdd = async () => {
    if (!targetId.trim()) return;

    setIsSubmitting(true);
    try {
      await assignToVault(vault.id, {
        targetType,
        targetId: targetId.trim(),
        priority: priority ? parseInt(priority, 10) : undefined,
      });
      setAddOpen(false);
      resetForm();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (assignmentTargetId: string) => {
    try {
      await removeFromVault(vault.id, assignmentTargetId);
    } catch {
      // Error handled in store
    }
  };

  const hierarchyLevel = VAULT_TYPE_PRIORITY[vault.type] ?? 3;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-foreground">Assignments</h4>
          <Badge variant="outline" className="text-xs">
            Priority: {hierarchyLevel}
          </Badge>
        </div>
        <Dialog open={addOpen} onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add Assignment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Assignment</DialogTitle>
              <DialogDescription>
                Assign a target to the "{vault.name}" vault.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="assign-target-type">Target Type</Label>
                <Select
                  value={targetType}
                  onValueChange={(value) => {
                    if (isVaultTargetType(value)) setTargetType(value);
                  }}
                >
                  <SelectTrigger id="assign-target-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                    <SelectItem value="app">App</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                    <SelectItem value="page">Page</SelectItem>
                    <SelectItem value="org">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <VaultTargetPicker
                targetType={targetType}
                targetId={targetId}
                onTargetIdChange={setTargetId}
              />
              <div className="space-y-2">
                <Label htmlFor="assign-priority">Priority (optional)</Label>
                <Input
                  id="assign-priority"
                  type="number"
                  placeholder="e.g., 1"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  min={0}
                />
                <p className="text-xs text-muted-foreground">
                  Lower numbers indicate higher priority
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setAddOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                disabled={!targetId.trim() || isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Assignment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {vault.assignments.length === 0 ? (
        <p className="py-2 text-center text-xs text-muted-foreground">
          No assignments yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Target ID</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {vault.assignments.map((assignment) => {
              const config = TARGET_TYPE_CONFIG[assignment.targetType];
              const Icon = config.icon;
              return (
                <TableRow key={`${assignment.targetType}-${assignment.targetId}`}>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={cn("gap-1 text-xs", config.color)}
                    >
                      <Icon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {assignment.targetId}
                  </TableCell>
                  <TableCell>{assignment.priority}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(assignment.assignedAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(assignment.targetId)}
                      disabled={isLoading}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
